import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Chart from 'react-apexcharts';
import qs from 'query-string';

import { useGetDistributorSubscribersDataQuery, useGetInvestmentFundsQuery } from 'api/main';
import { texts } from 'utils/languages/fr';
import { subscriptionStatus, queryStringifyOptions } from 'utils/constants';
import { parseFullName, parseSum } from 'utils/helpers';

import { ChartContainer, DashboardSection } from '../common-components';
import { CustomSelect, Loader } from 'components';
import { Typography, Box, MenuItem } from '@mui/material';
import {
    customTotalParser,
    parseCustomData,
    pieChartOptions,
    subscriptionNumberFormatter,
} from './AdminDistributorDashboard';

const getChartSubtitle = (data, category) => {
    const type = data === 'all' ? 'PRODUCT' : 'SHARE';
    return texts[`SUBSCRIPTIONS_${category}_BY_${type}`];
};

const getNoDataMessage = type => {
    if (type === 'product') {
        return texts.NO_SUBSCRIPTIONS_FOR_PODUCT;
    } else {
        return texts.NO_DISTIBUTOR_SUBSCRIPTIONS;
    }
};

const FinishedSubscriptionAnalytics = props => {
    const { isAdmin, distributors, distributorsLoader, classes } = props;

    const history = useHistory();
    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery();

    const [selectedFund, setSelectedFund] = useState('all');
    const [selectedDistributor, setSelectedDistributor] = useState('all');

    const { data: dataByFund = [], isFetching: dataByFundLoading } =
        useGetDistributorSubscribersDataQuery({ subscriptionStatus: subscriptionStatus.FINISHED });
    const { data: dataByDistributor = [], isFetching: dataByDistributorLoading } =
        useGetDistributorSubscribersDataQuery({
            cifId: selectedDistributor === 'all' ? undefined : selectedDistributor,
            subscriptionStatus: subscriptionStatus.FINISHED,
        });

    const filteredFundList = useMemo(
        () => fundList.filter(f => f.visible && !!dataByFund?.[f.id]),
        [fundList, dataByFund]
    );

    const totalValueByFund = useMemo(
        () => customTotalParser(dataByFund, selectedFund, 'sum_total_subscriptions'),
        [dataByFund, selectedFund]
    );
    const totalNumberByFund = useMemo(
        () => customTotalParser(dataByFund, selectedFund, 'total_subscriptions'),
        [dataByFund, selectedFund]
    );
    const totalValueByDistributor = useMemo(
        () => customTotalParser(dataByDistributor, 'all', 'sum_total_subscriptions'),
        [dataByDistributor]
    );
    const totalNumberByDistributor = useMemo(
        () => customTotalParser(dataByDistributor, 'all', 'total_subscriptions'),
        [dataByDistributor]
    );

    const handleInvestmentsByProduct = useCallback(() => {
        history.push({
            pathname: '/investors',
            search: `?${qs.stringify(
                { product: selectedFund === 'all' ? undefined : [selectedFund] },
                queryStringifyOptions
            )}`,
        });
    }, [history, selectedFund]);

    const handleInvestmentsByDistributor = useCallback(() => {
        history.push({
            pathname: '/investors',
            search: `?${qs.stringify(
                { dist: selectedDistributor === 'all' ? undefined : [selectedDistributor] },
                queryStringifyOptions
            )}`,
        });
    }, [history, selectedDistributor]);

    return (
        <DashboardSection title={texts.COMPLETED}>
            <Loader loading={fundsLoader || distributorsLoader} marginTop={10}>
                <Box mb={6}>
                    <Typography variant="subtitle1" gutterBottom>
                        <b>{texts.SUBSCRIPTIONS_BY_PRODUCT}</b>
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <CustomSelect
                            inputStyle={classes.filterInput}
                            value={selectedFund}
                            onChange={e => setSelectedFund(e.target.value)}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {filteredFundList.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Box>
                    <Loader loading={dataByFundLoading} marginTop={0} height="350px">
                        {!totalValueByFund && !totalNumberByFund && (
                            <Typography variant="subtitle2" gutterBottom>
                                {getNoDataMessage('product')}
                            </Typography>
                        )}
                        {!!totalValueByFund && !!totalNumberByFund && (
                            <Box height="350px" display="flex" alignItems="center">
                                <ChartContainer
                                    useSymbol
                                    handleClick={handleInvestmentsByProduct}
                                    classes={classes}
                                    title={texts.VALUE}
                                    number={totalValueByFund}
                                    subtitle={getChartSubtitle(selectedFund, 'VALUE')}
                                >
                                    <Chart
                                        type="pie"
                                        series={parseCustomData(
                                            dataByFund,
                                            selectedFund,
                                            'sum_total_subscriptions'
                                        )}
                                        options={{
                                            ...pieChartOptions,
                                            tooltip: {
                                                ...pieChartOptions.tooltip,
                                                y: { formatter: val => parseSum(val) },
                                            },
                                            labels:
                                                selectedFund === 'all'
                                                    ? filteredFundList.map(f => f.name)
                                                    : Object.keys(dataByFund?.[selectedFund] || {}),
                                        }}
                                    />
                                </ChartContainer>
                                <ChartContainer
                                    classes={classes}
                                    title={texts.NUMBER}
                                    number={totalNumberByFund}
                                    handleClick={handleInvestmentsByProduct}
                                    subtitle={getChartSubtitle(selectedFund, 'NUMBER')}
                                >
                                    <Chart
                                        type="pie"
                                        series={parseCustomData(
                                            dataByFund,
                                            selectedFund,
                                            'total_subscriptions'
                                        )}
                                        options={{
                                            ...pieChartOptions,
                                            tooltip: {
                                                ...pieChartOptions.tooltip,
                                                y: {
                                                    formatter: subscriptionNumberFormatter,
                                                },
                                            },
                                            labels:
                                                selectedFund === 'all'
                                                    ? filteredFundList.map(f => f.name)
                                                    : Object.keys(dataByFund?.[selectedFund] || {}),
                                        }}
                                    />
                                </ChartContainer>
                            </Box>
                        )}
                    </Loader>
                </Box>
                {isAdmin && (
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            <b>{texts.SUBSCRIPTIONS_BY_COMMERCIAL}</b>
                        </Typography>
                        <Loader loading={dataByDistributorLoading} marginTop={0} height="370px">
                            <Box display="flex" alignItems="center">
                                <CustomSelect
                                    inputStyle={classes.filterInput}
                                    value={selectedDistributor}
                                    onChange={e => setSelectedDistributor(e.target.value)}
                                >
                                    <MenuItem value="all">{texts.ALL_1}</MenuItem>
                                    {distributors.map(d => (
                                        <MenuItem key={d.id} value={d.user.id}>
                                            {parseFullName(d.user)}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                            </Box>
                            {!totalValueByDistributor && !totalNumberByDistributor && (
                                <Typography variant="subtitle2" gutterBottom>
                                    {getNoDataMessage('distributor')}
                                </Typography>
                            )}
                            {!!totalValueByDistributor && !!totalNumberByDistributor && (
                                <Box height="350px" display="flex" alignItems="center">
                                    <ChartContainer
                                        useSymbol
                                        classes={classes}
                                        title={texts.VALUE}
                                        number={totalValueByDistributor}
                                        handleClick={handleInvestmentsByDistributor}
                                        subtitle={
                                            totalValueByDistributor
                                                ? getChartSubtitle('all', 'VALUE')
                                                : ''
                                        }
                                    >
                                        <Chart
                                            type="pie"
                                            series={parseCustomData(
                                                dataByDistributor,
                                                'all',
                                                'sum_total_subscriptions'
                                            )}
                                            options={{
                                                ...pieChartOptions,
                                                tooltip: {
                                                    ...pieChartOptions.tooltip,
                                                    y: { formatter: val => parseSum(val) },
                                                },
                                                labels: filteredFundList.map(f => f.name),
                                            }}
                                        />
                                    </ChartContainer>
                                    <ChartContainer
                                        classes={classes}
                                        title={texts.NUMBER}
                                        number={totalNumberByDistributor}
                                        handleClick={handleInvestmentsByDistributor}
                                        subtitle={
                                            totalNumberByDistributor
                                                ? getChartSubtitle('all', 'NUMBER')
                                                : ''
                                        }
                                    >
                                        <Chart
                                            type="pie"
                                            series={parseCustomData(
                                                dataByDistributor,
                                                'all',
                                                'total_subscriptions'
                                            )}
                                            options={{
                                                ...pieChartOptions,
                                                tooltip: {
                                                    ...pieChartOptions.tooltip,
                                                    y: {
                                                        formatter: subscriptionNumberFormatter,
                                                    },
                                                },
                                                labels: filteredFundList.map(f => f.name),
                                            }}
                                        />
                                    </ChartContainer>
                                </Box>
                            )}
                        </Loader>
                    </Box>
                )}
            </Loader>
        </DashboardSection>
    );
};

export default FinishedSubscriptionAnalytics;
